define((require) => {
    "use strict";

    const EventDispatcher = require("shared/util/EventDispatcher");
    const EditorEvents    = require("shared/services/EditorEvents");
    const Preconditions   = require("shared/util/Preconditions");
    const _               = require("underscore");

    const {
        ADD_IMAGE_TAG,
        REMOVE_IMAGE_TAG,
        EDIT_IMAGE_TAG
    } = EditorEvents.constants;

    /**
     * Custom behavior for tracking `ImageTagCollection` related changes.
     *
     * Intentionally not a `Marionette.Behavior` subclass due to lack of event support.
     *
     * @class ImageTagTrackingBehavior
     */
    return EventDispatcher.extend("ImageTagTrackingBehavior", {

        /**
         * Constructor.
         *
         * @param {Object} options The options defined for `this` behavior.
         * @param {Backbone.View} view The associated view instance.
         */
        constructor(options, view) {
            this._view = view;
        },

        /**
         * Invoked via the Marionette behavior lifecycle.
         */
        onShow() {
            const {editable} = this._view.options;

            // Only enabled if the `editable` flag has been set.
            if (editable) {
                const {collection} = this._view;

                this.listenTo(collection, "add remove", this._addRemoveTagsHandler);
                this.listenTo(collection, "change", this._changeTagsHandler);
            }
        },

        /**
         * Invoked via the Marionette behavior lifecycle.
         */
        onClose() {
            this.stopListening();
        },

        _addRemoveTagsHandler(model, collection, {add, triggerSource}) {
            // Only trigger event if the associated view caused the change.
            if (triggerSource === this._view) {
                const eventName = add ? ADD_IMAGE_TAG : REMOVE_IMAGE_TAG;
                this._trigger(eventName, model);
            }
        },

        _changeTagsHandler(model, {stickitChange, editBehaviorChange}) {
            // Determine the view/trigger source causing the change - based on either stickit or the EditBehavior
            const view = (stickitChange && stickitChange.view) || (editBehaviorChange && editBehaviorChange.view);
            const {triggerSource} = view.options;

            // Ensure that the change origins from the associated view - we may have more than one view modifying the same model instance.
            if (triggerSource === this._view) {
                this._trigger(EDIT_IMAGE_TAG, model, {
                    previousTagName: model.previous("name"),
                    previousTagType: model.previous("type")
                });
            }
        },

        /**
         * Convenience for triggering the actual tracking event.
         *
         * Constructs the event properties based on the `ImageTagModel` and the current `ImageModel`.
         *
         * @param {String} eventName The event name.
         * @param {ImageTagModel} imageTagModel The current `ImageTagModel`.
         * @param {Object} [additionalEventProperties={}] Any additional event properties, optional.
         */
        _trigger(eventName, imageTagModel, additionalEventProperties = {}) {
            const {
                trackingOptions: {
                    triggerOrigin,
                    imageModel
                }
            } = this._view.options;

            Preconditions.isModel(imageModel, "An image model must be defined.");

            const eventProperties = {
                triggerOrigin,
                imageId: imageModel.get("id"),
                imageName: imageModel.get("name"),
                tagName: imageTagModel.get("name"),
                tagType: imageTagModel.get("type")
            };

            _.extend(eventProperties, additionalEventProperties);

            EditorEvents.trigger(eventName, eventProperties);
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/assets/image/tags/ImageTagTrackingBehavior.js